import createSelector from 'lib/createSelector'
import config from 'constants/config'
import { isStripe3dsEnabled } from 'lib/stripeUtils'
import { geoCriteriaMetForCalendarRecommendations } from 'lib/recommendationUtils'
import { updateExperimentContext } from 'analytics/snowplow'
import { isEnabledForFeature } from 'lib/featureFlagUtils'
import { isMobileDevice } from './configSelectors'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import { EMPLOYEE_ROLE } from 'constants/auth'

export const getLereSerendipityVariant = createSelector(
  (state: App.State) => state.config.experiments[config.LERE_SERENDIPITY_TILES_VARIANT_EXPERIMENT_ID],
  (experimentValue) => experimentValue,
)

export const getLereLocationFlexSerendipityVariant = createSelector(
  (state: App.State) => state.config.experiments[config.LERE_LOCATION_FLEX_SERENDIPITY_VARIANT_EXPERIMENT_ID],
  (experimentValue) => experimentValue,
)

export const getLereNonLppStickyFooterEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.LERE_ENABLE_NON_LPP_OFFER_STICKY_FOOTER_EXPERIMENT_ID],
  (experimentValue) => experimentValue === '1',
)

export const getLereExpiredDealsStickyFooterEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.LERE_ENABLE_EXPIRED_DEALS_STICKY_FOOTER_EXPERIMENT_ID],
  (experimentValue) => experimentValue === '1',
)

export const getLereHotelSRPStickyFooterOnScrollEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.LERE_HOTEL_SEARCH_PAGE_STICKY_FOOTER_ON_SCROLL_EXPERIMENT_ID],
  (experimentValue) => experimentValue === '1',
)

export const getLereHotelSRPStickyFooterOnIdleEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.LERE_HOTEL_SEARCH_PAGE_STICKY_FOOTER_ON_IDLE_EXPERIMENT_ID],
  (experimentValue) => experimentValue === '1',
)

export type LEREBooleanExperimentVariant = 'pending' | 'enabled' | 'disabled' | 'notEnrolled'

export const getLereInContentYmalEnabledVariant = createSelector(
  (state: App.State) => {
    return state.config.experiments[config.LERE_IN_CONTENT_YMAL_EXPERIMENT_ID]
  },
  (experimentValue): LEREBooleanExperimentVariant => {
    if (!config.LERE_IN_CONTENT_YMAL_EXPERIMENT_ID) {
      return 'notEnrolled'
    }
    switch (experimentValue) {
      case '0':
        updateExperimentContext(config.LERE_IN_CONTENT_YMAL_EXPERIMENT_ID, experimentValue)
        return 'disabled'
      case '1':
        updateExperimentContext(config.LERE_IN_CONTENT_YMAL_EXPERIMENT_ID, experimentValue)
        return 'enabled'
      default:
        return 'pending'
    }
  },
)

export const getLereChallengerEnabledVariant = createSelector(
  (state: App.State) => state.config.experiments[config.LERE_ENABLE_CHALLENGER_EXPERIMENT_ID],
  (experimentValue): LEREBooleanExperimentVariant => {
    if (!config.LERE_ENABLE_CHALLENGER_EXPERIMENT_ID) {
      return 'notEnrolled'
    }
    switch (experimentValue) {
      case '0':
        updateExperimentContext(config.LERE_ENABLE_CHALLENGER_EXPERIMENT_ID, experimentValue)
        return 'disabled'
      case '1':
        updateExperimentContext(config.LERE_ENABLE_CHALLENGER_EXPERIMENT_ID, experimentValue)
        return 'enabled'
      default:
        return 'pending'
    }
  },
)

export const getLerePersonalisedAbandonedCartsExperimentVariant = createSelector(
  (state: App.State) => state.config.experiments[config.LERE_ENABLE_PERSONALISED_ABANDONED_CARTS_EXPERIMENT_ID],
  (experimentValue: '0' | '1' | '2' | undefined): App.LerePersonalisedAbandonedCartsVariant | 'disabled' | 'unavailable' => {
    if (!config.LERE_ENABLE_PERSONALISED_ABANDONED_CARTS_EXPERIMENT_ID) {
      return 'unavailable'
    }
    if (experimentValue === '1') {
      updateExperimentContext(config.LERE_ENABLE_PERSONALISED_ABANDONED_CARTS_EXPERIMENT_ID, experimentValue)
      return 'naive-model'
    }
    if (experimentValue === '2') {
      updateExperimentContext(config.LERE_ENABLE_PERSONALISED_ABANDONED_CARTS_EXPERIMENT_ID, experimentValue)
      return 'optimised-model'
    }
    return 'disabled'
  },
)

export const shouldEnableCalendarRecommendations = createSelector(
  (state: App.State, offer: App.Offer | undefined) => offer,
  (offer) => geoCriteriaMetForCalendarRecommendations(offer),
)

export const isStripe3dsAvailable = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (currentCurrency): boolean => (isStripe3dsEnabled(currentCurrency)),
)

export const standaloneFlightsUniversalCheckoutEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_UNIVERSAL_CHECKOUT_STANDALONE_FLIGHTS_ID],
  (state: App.State) => state.auth.account.isSpoofed,
  (experiment, isSpoofed): boolean => config.UNIVERSAL_CHECKOUT_STANDALONE_FLIGHTS_ENABLED && experiment === '1' && !isSpoofed,
)

export const isSpoofed = createSelector(
  (state: App.State) => state.auth.account.isSpoofed,
  (isSpoofed) => isSpoofed,
)

export const isCustomerSupportMode = createSelector(
  isSpoofed,
  (state: App.State) => state.config.previewMode,
  (state: App.State) => state.config.storeMode,
  (isSpoofed, isPreviewMode, isStoreMode) => isSpoofed || isPreviewMode || isStoreMode,
)

export const shouldShowSalesUSP = createSelector(
  (_state: App.State, lowestPricedPackageTrackingPrice: number, threshold: number) => lowestPricedPackageTrackingPrice >= threshold,
  (state: App.State) => state.geo.currentRegionCode,
  (isPriceOverThreshold, currentRegion): boolean => isEnabledForFeature('ALL/IN', currentRegion) && isPriceOverThreshold,
)

export const multiRoomEnabled = createSelector(
  (_state: App.State, offer: App.Offer) => isEnabledForFeature(config.MULTIPLE_ROOM_TYPE_BOOKING_ENABLED_LIST, offer?.type),
  (roomTypeEnabled) => config.MULTIPLE_ROOM_TYPE_BOOKING_ENABLED && roomTypeEnabled,
)

export const shouldShowContinueToApp = createSelector(
  isMobileDevice,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.config.headlessMode,
  (state: App.State) => state.config.appBannerCookie,
  (state: App.State) => state.utm.medium,
  (state: App.State) => !!state.router.location.query.gclid,
  (state: App.State) => state.config.originalCanonicalURL,
  (isMobileDevice, regionCode, isHeadlessMode, appBannerCookie, utmMedium, hasGoogleClickIdentifier, url): boolean =>
    config.CONTINUE_TO_APP_BANNER &&
      isMobileDevice &&
      !isHeadlessMode &&
      !appBannerCookie &&
      (utmMedium !== 'cpc' || (utmMedium === 'cpc' ||
        hasGoogleClickIdentifier) &&
        !!config.appBanner?.promotionData[regionCode]?.specialCodeForAdClicks) &&
      !url.includes('top-picks-for-me'),
)

export const getInstalmentsAbTestEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.PAYMENTS_INSTALMENTS_EXPERIMENT_ID],
  (experiment) => {
    return experiment === '1' || experiment === '2'
  },
)

export const newOfferTileAndLandingPageEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_NEW_OFFER_TILE_AND_LANDING_PAGE],
  (experiment) => {
    return experiment === '1'
  },
)

export const cruiseSupportBanner = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_CRUISE_SUPPORT_BANNER_EXPERIMENT_ID],
  (experiment) => {
    return experiment === '1'
  },
)

export const toursPricePerDayABTestVariation = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_TOURS_PRICE_PER_DAY],
  (experiment) => experiment,
)

export const isSkipExperiencesTestEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_SKIP_EXPERIENCES_EXPERIMENT_ID], isCustomerSupportMode,
  (experiment, isCustomerSupportMode) => {
    return experiment === '1' && !isCustomerSupportMode
  },
)

export const isScooponHomeEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_SCOOPON_ACCOMMODATION_HOMEPAGE_EXPERIMENT_ID],
  (experiment): boolean => {
    return config.BRAND === 'scoopontravel' && experiment === '1'
  },
)

export const isReserveVsPayWordingForDepositsEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_RESERVE_VS_PAY_WORDING_FOR_DEPOSITS_EXPERIMENT_ID],
  (experiment): boolean => {
    return experiment === '1'
  },
)

export const isOfferPageCuratedTripBannerEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_CURATED_TRIPS_OFFER_PAGE_PROMOTION],
  (experimentValue): boolean => {
    return experimentValue === '1'
  },
)

export const hotelSearchPageCuratedTripBannerPosition = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_CURATED_TRIPS_HOTEL_SEARCH_PAGE_PROMOTION],
  (experimentValue): number | undefined => {
    if (experimentValue === '1') { return 3 }
    if (experimentValue === '2') { return 5 }
  },
)

export const trendingDestinationsAbTestVariant = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_TRENDING_LOCATIONS_COPY],
  (experimentValue): string => {
    return (experimentValue === '1' || experimentValue === '2') ? experimentValue : undefined
  },
)

export const isUserReviewsAbTestEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_USER_REVIEWS],
  (state: App.State) => state.utm.medium,
  (experiment, utmMedium) => {
    return experiment === '2' && utmMedium !== 'cpc' && config.userReviews.isEnabled
  },
)

export const isIntroMapEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_MAP_POSITION],
  (experimentValue): boolean => {
    return experimentValue === '1'
  },
)

export const isUkSignUpEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_UK_SIGN_UP],
  (state: App.State) => state.geo.currentRegionCode,
  (experimentValue, currentRegionCode): boolean => {
    return experimentValue === '1' && currentRegionCode === 'GB'
  },
)

export const isNewHomePageEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_NEW_HOME_PAGE] || state.config.experiments[config.AB_TEST_NEW_HOME_PAGE_MOBILE],
  (state: App.State) => state.geo.currentRegionCode,
  (experiment, currentRegionCode): boolean => {
    return experiment === '1' && isEnabledForFeature(config.HOME_PAGE_TEST_REGIONS, currentRegionCode)
  },
)

export const isVelocityEnabled = createSelector(
  (state: App.State) => state.geo.partnership?.code,
  (partnershipCode): boolean => partnershipCode === VIRGIN_VELOCITY_PARTNERSHIP_CODE && !!config.VELOCITY_SSO_URL,
)

export const isDestinationLandingPagePreviewEnabled = createSelector(
  (state: App.State) => state.auth,
  (auth) => {
    const isProd = config.DESTINATION_LANDING_PAGE_ENABLED === 'employees'
    return isProd ? auth.account?.roles.includes(EMPLOYEE_ROLE) : true
  },
)

export const isPayButtonTestEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_PAY_BUTTON_COPY],
  (experimentValue): boolean => {
    return experimentValue === '1'
  },
)

export const isNortonBadgeDisabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_REMOVE_NORTON_BADGE_AU],
  (state: App.State) => state.config.experiments[config.AB_TEST_REMOVE_NORTON_BADGE_US],
  (state: App.State) => state.config.experiments[config.AB_TEST_REMOVE_NORTON_BADGE_UK],
  (auExperiment, usExperiment, ukExperiment): boolean => {
    return auExperiment === '1' || usExperiment === '1' || ukExperiment === '1'
  },
)

export const getUkSignUpTimerVariantDesktop = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_UK_SIGN_UP_TIMER_DESKTOP],
  (state: App.State) => state.geo.currentRegionCode,
  (experimentValue, currentRegionCode): string | undefined => {
    return (experimentValue === '1' || experimentValue === '2' || experimentValue === '3') && currentRegionCode === 'GB' ? experimentValue : undefined
  },
)

export const getUkSignUpTimerVariantMobile = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_UK_SIGN_UP_TIMER_MOBILE],
  (state: App.State) => state.geo.currentRegionCode,
  (experimentValue, currentRegionCode): string | undefined => {
    return (experimentValue === '1' || experimentValue === '2' || experimentValue === '3') && currentRegionCode === 'GB' ? experimentValue : undefined
  },
)

export const isDesktopUkAtolBannerEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_UK_ATOL_BANNER_DESKTOP],
  (state: App.State) => state.geo.currentRegionCode,
  (experimentValue, currentRegionCode): boolean => {
    return experimentValue === '1' && currentRegionCode === 'GB'
  },
)

export const isMobileUkAtolBannerEnabled = createSelector(
  (state: App.State) => state.config.experiments[config.AB_TEST_UK_ATOL_BANNER_MOBILE],
  (state: App.State) => state.geo.currentRegionCode,
  (experimentValue, currentRegionCode): boolean => {
    return experimentValue === '1' && currentRegionCode === 'GB'
  },
)
